<template>
  <v-dialog v-model="dialog"
            :fullscreen="$root.dialogSettings.fullscreen"
            :scrollable="$root.dialogSettings.scrollable"
            :hide-overlay="$root.dialogSettings.hideoverlay"
            :transition="$root.dialogSettings.transition"
            :width="$root.dialogSettings.width"
            :max-width="$root.dialogSettings.maxwidth">
    <template v-slot:activator="{ on }">
      <v-btn text
             v-on="on">
        {{ $t('summary') }}
      </v-btn>
    </template>

    <v-card class="secondary">
      <v-container fluid
                   class="px-5">
        <v-row v-if="!skipOrder">
          <v-col cols="12">
            <v-card outlined>
              <v-card-title class="text-h5 primary white--text">
                {{ $t('stepper.orderInfo') }}
              </v-card-title>
              <v-card-text class="mt-3">
                <v-row dense>
                  <v-col cols="12">
                    <h5 class="mt-3">
                      {{ $t('stepper.order.basic') }}
                    </h5>
                  </v-col>
                  <summary-item :validator="$root.$v.order.basicInformation.claimType"
                                label="claimType"
                                :text="$t('claimType.' + order.basicInformation.claimType)" />
                  <summary-item :validator="$root.$v.order.basicInformation.customerNr"
                                label="customerNr" />
                  <summary-item :validator="$root.$v.order.basicInformation.information"
                                label="additionalInformation" />
                  <v-col cols="12">
                    <h5 class="mt-3">
                      {{ $t('stepper.order.debtors') }}
                    </h5>
                    <v-row v-for="(debtor, i) in debtors"
                           :key="i"
                           dense>
                      <v-col cols="12">
                        <h6 v-if="debtor.mainDebtor && debtors.length > 1"
                            class="mt-0">
                          Päävelallinen
                        </h6>
                        <h6 v-if="!debtor.mainDebtor && debtors.length > 1"
                            class="mt-0">
                          Osavelallinen
                        </h6>
                      </v-col>
                      <summary-item v-if="debtor.isCompany"
                                    :validator="$root.$v.order.debtors.$each[i].companyBusinessId"
                                    label="businessId" />
                      <summary-item v-if="debtor.isCompany"
                                    :validator="$root.$v.order.debtors.$each[i].companyName"
                                    label="companyName" />
                      <summary-item v-if="!debtor.isCompany"
                                    :validator="$root.$v.order.debtors.$each[i].firstName"
                                    label="firstName" />
                      <summary-item v-if="!debtor.isCompany"
                                    :validator="$root.$v.order.debtors.$each[i].lastName"
                                    label="lastName" />
                      <summary-item v-if="!debtor.isCompany"
                                    :validator="$root.$v.order.debtors.$each[i].socSecNr"
                                    label="socSecNr" />
                      <summary-item v-if="!debtor.isCompany"
                                    :validator="$root.$v.order.debtors.$each[i].language"
                                    label="language"
                                    :text="$t('languages.' + debtor.language)" />
                      <summary-item :validator="$root.$v.order.debtors.$each[i].streetAddress"
                                    label="streetAddress" />
                      <summary-item :validator="$root.$v.order.debtors.$each[i].postalNumber"
                                    label="postalNumber" />
                      <summary-item :validator="$root.$v.order.debtors.$each[i].city"
                                    label="city" />
                      <summary-item :validator="$root.$v.order.debtors.$each[i].phone"
                                    label="phone" />
                      <summary-item :validator="$root.$v.order.debtors.$each[i].email"
                                    label="email" />
                      <summary-item :validator="$root.$v.order.debtors.$each[i].country"
                                    label="country" />
                    </v-row>
                  </v-col>
                  <v-col cols="12">
                    <h5 class="mt-3">
                      {{ $t('bill.header') }}
                    </h5>
                    <v-row v-for="(bill, i) in bills"
                           :key="i"
                           dense>
                      <v-col cols="12">
                        <h5 v-if="bills.length > 1">
                          {{ $t('common.bill') }} {{ bill.nr }}
                        </h5>
                      </v-col>

                      <summary-item :validator="$root.$v.order.bills.$each[i].amount"
                                    label="amount" />
                      <summary-item :validator="$root.$v.order.bills.$each[i].information"
                                    label="information" />
                      <summary-item :validator="$root.$v.order.bills.$each[i].billNr"
                                    label="billNr" />
                      <summary-item :validator="$root.$v.order.bills.$each[i].referenceNr"
                                    label="referenceNr" />
                      <summary-item :validator="$root.$v.order.bills.$each[i].billDate"
                                    label="billDate"
                                    :text="bill.billDate | formatDate" />
                      <summary-item :validator="$root.$v.order.bills.$each[i].dueDate"
                                    label="dueDate"
                                    :text="bill.dueDate | formatDate" />
                      <summary-item v-if="bill.interestType !== 1"
                                    :validator="$root.$v.order.bills.$each[i].interestType"
                                    label="interestType"
                                    :text="$t('labels.interest.' + bill.interestType)" />
                      <summary-item v-if="bill.interestType === 1"
                                    :validator="$root.$v.order.bills.$each[i].interest"
                                    label="interestType"
                                    :text="$t('labels.interest.' + bill.interestType) + ' ' + bill.interest + ' %'" />
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-card-title class="text-h5 primary white--text">
                {{ $t('client.header') }}
              </v-card-title>
              <v-card-text class="mt-3">
                <v-row dense>
                  <summary-item v-if="!client.isCompany"
                                :validator="$root.$v.client.firstName"
                                label="firstName" />
                  <summary-item v-if="!client.isCompany"
                                :validator="$root.$v.client.lastName"
                                label="lastName" />
                  <summary-item v-if="client.isCompany"
                                :validator="$root.$v.client.companyType"
                                label="companyType"
                                :text="client.companyType !== '' ? $t('companyType.' + client.companyType) : ''" />
                  <summary-item v-if="client.isCompany"
                                :validator="$root.$v.client.businessId"
                                label="businessId" />
                  <summary-item v-if="client.isCompany"
                                :validator="$root.$v.client.companyName"
                                label="companyName" />
                  <summary-item :validator="$root.$v.client.address"
                                label="address" />
                  <summary-item :validator="$root.$v.client.postalNumber"
                                label="postalNumber" />
                  <summary-item :validator="$root.$v.client.city"
                                label="city" />
                  <summary-item v-if="!client.isCompany"
                                :validator="$root.$v.client.phoneNumber"
                                label="phoneNumber" />
                  <summary-item v-if="!client.isCompany"
                                :validator="$root.$v.client.email"
                                label="email" />
                  <summary-item v-if="!client.isCompany"
                                :validator="$root.$v.client.socSecNr"
                                label="socSecNr" />
                  <summary-item :validator="$root.$v.client.IBAN"
                                label="IBAN" />
                  <summary-item v-if="$root.BICIsRequired"
                                :validator="$root.$v.client.BIC"
                                label="BIC" />
                  <summary-item :validator="$root.$v.client.vatObligation"
                                label="vatObligation"
                                bool12 />

                  <template v-if="client.isCompany">
                    <v-col cols="12">
                      <h5 class="mt-3">
                        {{ $t('client.contactPerson') }}
                      </h5>
                    </v-col>
                    <summary-item :validator="$root.$v.client.contactPerson.firstName"
                                  label="firstName" />
                    <summary-item :validator="$root.$v.client.contactPerson.lastName"
                                  label="lastName" />
                    <summary-item :validator="$root.$v.client.contactPerson.phoneNumber"
                                  label="phoneNumber" />
                    <summary-item :validator="$root.$v.client.contactPerson.email"
                                  label="email" />
                  </template>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-card-title class="text-h5 primary white--text">
                {{ $t('moneyLaundering.header') }}
              </v-card-title>
              <v-card-text class="mt-3">
                <v-row dense>
                  <summary-item :validator="$root.$v.moneyLaundering.typicalClaim"
                                label="typicalClaim"
                                :text="moneyLaundering.typicalClaim !== '' ? $t('typicalClaim.' + moneyLaundering.typicalClaim) : ''" />
                  <summary-item :validator="$root.$v.moneyLaundering.capitalClassification"
                                label="capitalClassification"
                                :text="moneyLaundering.capitalClassificationText" />
                  <summary-item v-if="!moneyLaundering.politicallyInfluentialPerson"
                                :validator="$root.$v.moneyLaundering.politicallyInfluentialPerson"
                                label="politicallyInfluentialPerson"
                                bool />
                  <summary-item v-if="moneyLaundering.politicallyInfluentialPerson"
                                :validator="$root.$v.moneyLaundering.politicallyInfluentialPersonStatus"
                                label="politicallyInfluentialPerson" />
                  <template v-if="client.isCompany">
                    <summary-item :validator="$root.$v.moneyLaundering.hasNotificationObligation"
                                  label="hasNotificationObligation"
                                  bool />
                    <summary-item v-if="!moneyLaundering.needsOperatingLicense || moneyLaundering.needsOperatingLicense === ''"
                                  :validator="$root.$v.moneyLaundering.needsOperatingLicense"
                                  label="needsOperatingLicense"
                                  bool />
                    <summary-item v-if="moneyLaundering.needsOperatingLicense"
                                  :validator="$root.$v.moneyLaundering.operatingLicenseNameAndAuthority"
                                  label="operatingLicenseNameAndAuthority" />
                    <summary-item :validator="$root.$v.moneyLaundering.foreignGroupCompanyPlace"
                                  label="foreignGroupCompany"
                                  :text="foreignCompanyText" />
                    <summary-item v-if="moneyLaundering.foreignGroupCompany === 2"
                                  :validator="$root.$v.moneyLaundering.hasBigShareholders"
                                  label="hasBigShareholders"
                                  :text="moneyLaundering.hasBigShareholders ? $t('moneyLaundering.hasBigShareholdersTrue') : $t('moneyLaundering.hasBigShareholdersFalse')" />
                    <summary-item v-if="moneyLaundering.foreignGroupCompany === 2 && moneyLaundering.hasBigShareholders && moneyLaundering.hasPublicStockExchangeInEUCountry"
                                  :validator="$root.$v.moneyLaundering.publicStockExchangeInEUCountry"
                                  label="publicStockExchangeInEUCountryAnswer" />
                    <v-col v-if="moneyLaundering.foreignGroupCompany === 2 && moneyLaundering.hasBigShareholders && !moneyLaundering.hasPublicStockExchangeInEUCountry"
                           cols="12"
                           sm="6">
                      <label class="summary-label" :class="{ 'error--text': $root.$v.moneyLaundering.shareholders.$invalid }">
                        {{ $t('labels.hasPublicStockExchangeInEUCountry') }}
                      </label>
                      <span v-for="shareHolder in moneyLaundering.shareholders"
                            :key="shareHolder.name"
                            class="summary-text">
                        {{ shareHolder.name }}
                      </span>
                    </v-col>
                    <summary-item v-if="moneyLaundering.companyRepresentative.isRepresentative === 'companyRepresentative' || moneyLaundering.companyRepresentative.isRepresentative === null"
                                  :validator="$root.$v.moneyLaundering.companyRepresentative.isRepresentative"
                                  label="companyRepresentativeClaim"
                                  :text="moneyLaundering.companyRepresentative.isRepresentative !== null ? $t('labels.isCompanyRepresentative') : ''" />
                    <summary-item v-if="moneyLaundering.companyRepresentative.isRepresentative === 'powerOfAttorney'"
                                  :validator="$root.$v.moneyLaundering.companyRepresentative.file"
                                  label="companyRepresentativeClaim"
                                  :text="$t('labels.powerOfAttorneyFile')" />
                    <summary-item v-if="moneyLaundering.companyRepresentative.isRepresentative === 'position'"
                                  :validator="$root.$v.moneyLaundering.companyRepresentative.isRepresentative"
                                  label="companyRepresentativeClaim"
                                  :text="$t('moneyLaundering.companyRepresentativePosition') + moneyLaundering.companyRepresentative.positionInCompany" />
                  </template>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary"
                 text
                 @click="dialog = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
  import SummaryItem from './SummaryItem.vue'

  export default {
    components: {
      'summary-item': SummaryItem
    },
    data: () => ({
      dialog: false
    }),
    computed: {
      skipOrder () {
        return this.$store.getters.skipOrder
      },
      order () {
        return this.$store.getters.order
      },
      orderBasicInformation () {
        return this.order.basicInformation
      },
      debtors () {
        return this.order.debtors
      },
      bills () {
        return this.order.bills
      },
      client () {
        return this.$store.getters.client
      },
      moneyLaundering () {
        return this.$store.getters.moneyLaundering
      },
      foreignCompanyText () {
        if (!this.client.isCompany) {
          return ''
        } else if (this.moneyLaundering.foreignGroupCompany === 0) {
          return this.$t('common.no')
        } else if (this.moneyLaundering.foreignGroupCompany === 1) {
          return this.$t('moneyLaundering.foreignGroupCompanyAnswer-1')
        } else if (this.moneyLaundering.foreignGroupCompany === 2) {
          return this.moneyLaundering.foreignGroupCompanyPlace
        } else {
          return ''
        }
      }
    }
  }
</script>

<style>
    .summary-label {
        font-weight: 600;
        font-size: 0.85rem;
    }
    .summary-text {
        font-size: 0.85rem;
        /* font-weight: 600;*/
        margin-left: 0.5rem;
    }
    .summary-label.required:after {
        content: " *:";
    }
    .summary-label:after {
        content: ":";
    }
</style>
